import { Cart, type CheckoutMyRequestBody } from '@tix/entity'
import { isNotFoundError } from '@tix/openapi-sdk'
import { retrievePaymentIntent } from '@tix/stripe'

import { DONATION_PURCHASE_CART_EMBEDS } from '@/helpers/donationGa4'
import { getEnv } from '@/helpers/environment'
import { mapCheckoutResponse } from '@/helpers/stripeHelpers'

const SUCCESS_PAYMENT_INTENT_STATUSES = new Set(['requires_capture', 'processing', 'succeeded'])

export type PaymentReturnQuery = {
  cartId: string
  purchaserId?: string
  sourceType: string
  clientSecret: string
}

/**
 * Base payment-return URL with nested confirmation and retry destination paths. Cart/receipt/payment settlement params are layered later.
 */
export function buildPaymentReturnUrl(params: { confirmationPath: string; retryPath: string }): URL {
  return new URL(
    `/payment-return?${new URLSearchParams({
      confirmationPath: params.confirmationPath,
      retryPath: params.retryPath,
    })}`,
    window.location.origin,
  )
}

/**
 * Donation payment-return URL for an event template: confirmation → `/donations/:id/complete`, retry → `/donations/:id`.
 */
export function buildDonationsPaymentReturnUrl(eventTemplateId: string): URL {
  return buildPaymentReturnUrl({
    confirmationPath: `/donations/${eventTemplateId}/complete`,
    retryPath: `/donations/${eventTemplateId}`,
  })
}

/**
 * Layers cartId and receipt email onto the payment-return URL. Email is written onto the nested confirmationPath (destination owns it); payment-return only carries payment settlement fields.
 */
export function buildStripeReturnUrl(params: { returnUrl: URL; cartId: string; receiptEmail: string }): URL {
  const returnParams = Object.fromEntries(params.returnUrl.searchParams)
  const confirmation = new URL(returnParams.confirmationPath ?? '', params.returnUrl.origin)
  const confirmationPath = params.receiptEmail
    ? `${confirmation.pathname}?${new URLSearchParams({
        ...Object.fromEntries(confirmation.searchParams),
        email: params.receiptEmail,
      })}`
    : `${confirmation.pathname}${confirmation.search}`

  return new URL(
    `?${new URLSearchParams({
      ...returnParams,
      confirmationPath,
      cartId: params.cartId,
    })}`,
    params.returnUrl,
  )
}

/**
 * Appends confirm-time payment settlement params (source_type, optional purchaserId) for Stripe `return_url`.
 */
export function buildStripeConfirmReturnUrl(params: {
  returnUrl: URL
  sourceType: string
  purchaserId?: string
}): string {
  return new URL(
    `?${new URLSearchParams({
      ...Object.fromEntries(params.returnUrl.searchParams),
      source_type: params.sourceType,
      ...(params.purchaserId ? { purchaserId: params.purchaserId } : {}),
    })}`,
    params.returnUrl,
  ).toString()
}

/**
 * Builds the post-payment settlement confirmation destination path. Merges order/amount onto any query already on confirmationPath (e.g. receipt email). PaymentIntent amount is Stripe minor units; complete routes / PriceDisplay expect major units.
 */
export function buildCompletedConfirmationPath(params: {
  confirmationPath: string
  orderNumber: string
  amountMinor: number
}): string {
  const base = new URL(params.confirmationPath, window.location.origin)
  return `${base.pathname}?${new URLSearchParams({
    ...Object.fromEntries(base.searchParams),
    order: params.orderNumber,
    amount: String(params.amountMinor / 100),
  })}`
}

export type SettlePaymentReturnResult =
  | { status: 'completed'; orderNumber: string; amountMinor: number; cart: Cart }
  | { status: 'cancelled'; message: string }
  | { status: 'cart_error'; message: string }
  | { status: 'payment_error'; message: string }
  | { status: 'unexpected'; message: string }

/**
 * After a redirect-only payment method returns, retrieve the PaymentIntent and settle the cart (no captcha).
 */
export async function settlePaymentReturn(query: PaymentReturnQuery): Promise<SettlePaymentReturnResult> {
  try {
    // Embeds let the caller rebuild analytics payloads that the page which created the cart is no longer around to supply.
    const cartResult = await Cart.loadMy(query.cartId, { _embed: [...DONATION_PURCHASE_CART_EMBEDS] })
    if (cartResult.status !== 'success' || !cartResult.data) {
      const message = isNotFoundError(cartResult.error)
        ? 'Your cart is no longer available. Please try again.'
        : 'Unable to load your cart. Please try again.'
      return { status: 'cart_error', message }
    }

    const cart = cartResult.data
    const accountId = cart.stripeFee?.account_id

    if (!accountId) return { status: 'cart_error', message: 'Cart not booked — no Stripe account available' }

    const intentResult = await retrievePaymentIntent({
      publishableKey: getEnv().config.stripe.publishable_key,
      accountId,
      clientSecret: query.clientSecret,
    })

    if (!intentResult.paymentIntent) {
      const message = intentResult.stripeError?.message ?? intentResult.error?.message ?? 'Unable to verify payment'
      return { status: 'payment_error', message }
    }

    const { paymentIntent } = intentResult

    const isSuccess = SUCCESS_PAYMENT_INTENT_STATUSES.has(paymentIntent.status)
    if (isSuccess) {
      // Same embeds as loadMy so applyCartResponseData keeps tickets / groups / types after a ticket_order settle.
      const checkoutEmbed: NonNullable<CheckoutMyRequestBody['_embed']> = [...DONATION_PURCHASE_CART_EMBEDS]

      const checkoutResponse = query.purchaserId
        ? await cart.guestCheckoutMy({
            gateway_id: 'stripe',
            gateway_data: { source: paymentIntent.id, source_type: query.sourceType },
            guest_identity_id: query.purchaserId,
            _embed: checkoutEmbed,
          })
        : await cart.checkoutMy({
            gateway_id: 'stripe',
            gateway_data: { source: paymentIntent.id, source_type: query.sourceType },
            _embed: checkoutEmbed,
          })

      const mappedCheckoutResponse = mapCheckoutResponse(checkoutResponse)
      if (mappedCheckoutResponse.status === 'completed') {
        return {
          status: 'completed',
          orderNumber: mappedCheckoutResponse.data.ticket_order?._data?.[0]?.order_number ?? '—',
          amountMinor: paymentIntent.amount,
          cart,
        }
      }

      if (mappedCheckoutResponse.status === 'requires_action') {
        return { status: 'unexpected', message: 'Unexpected payment action required after redirect' }
      }

      return { status: 'unexpected', message: mappedCheckoutResponse.error.message }
    }

    if (paymentIntent.status === 'requires_payment_method') {
      return {
        status: 'cancelled',
        message: paymentIntent.last_payment_error?.message ?? 'Payment was cancelled. Please try again.',
      }
    }

    if (paymentIntent.last_payment_error?.message) {
      return { status: 'payment_error', message: paymentIntent.last_payment_error.message }
    }

    return { status: 'unexpected', message: 'An unexpected error occurred while completing payment.' }
  } catch (e) {
    return {
      status: 'unexpected',
      message: e instanceof Error ? e.message : 'An unexpected error occurred while completing payment.',
    }
  }
}
